import { z } from "zod";
import type { PerformanceKpi } from "@/components/shared/performance/performance";
import type { DashboardPerformanceMetric } from "@/lib/dashboard-sales";

/**
 * Shared metric catalog for the dashboard KPI strip, its metric menu and the
 * Performance chart, so the three never disagree on units, axis or grouping.
 */

export type DashboardMetricFamily = "currency" | "percent" | "count" | "ratio";
export type DashboardMetricGroup = "sales" | "orders" | "cost" | "efficiency" | "traffic";
export type DashboardCompareMode = "previous_period" | "off";

export const DASHBOARD_METRIC_KEYS = [
  "totalSales",
  "spend",
  "sales",
  "totalOrders",
  "orders",
  "acos",
  "tacos",
  "cpc",
  "cpa",
  "roas",
  "clicks",
  "impressions",
  "ctr",
  "cvr",
] as const satisfies readonly DashboardPerformanceMetric[];

export const dashboardMetricSchema = z.enum(DASHBOARD_METRIC_KEYS);

export const DASHBOARD_METRIC_GROUPS: readonly {
  group: DashboardMetricGroup;
  metrics: readonly DashboardPerformanceMetric[];
}[] = [
  { group: "sales", metrics: ["totalSales", "sales"] },
  { group: "orders", metrics: ["totalOrders", "orders"] },
  { group: "cost", metrics: ["spend", "cpc", "cpa"] },
  { group: "efficiency", metrics: ["acos", "tacos", "roas", "ctr", "cvr"] },
  { group: "traffic", metrics: ["clicks", "impressions"] },
];

export const METRIC_FAMILY: Record<DashboardPerformanceMetric, DashboardMetricFamily> = {
  totalSales: "currency",
  spend: "currency",
  sales: "currency",
  cpc: "currency",
  cpa: "currency",
  acos: "percent",
  tacos: "percent",
  ctr: "percent",
  cvr: "percent",
  totalOrders: "count",
  orders: "count",
  clicks: "count",
  impressions: "count",
  roas: "ratio",
};

/** Six cards, in the order the Shop Dashboard design shows them. */
export const DEFAULT_KPI_SLOTS: readonly DashboardPerformanceMetric[] = [
  "totalSales",
  "spend",
  "sales",
  "acos",
  "tacos",
  "totalOrders",
];

/**
 * The card dot matches the metric's color in the Performance chart; metrics the
 * chart never draws in color stay neutral.
 */
export const METRIC_DOT_COLOR: Partial<Record<DashboardPerformanceMetric, string>> = {
  sales: "var(--color-chart-1)",
  spend: "var(--color-chart-2)",
  acos: "var(--color-chart-3)",
  orders: "var(--color-chart-4)",
};

export type PerformanceView = "sales" | "orders";
export const PERFORMANCE_VIEWS = ["sales", "orders"] as const satisfies readonly PerformanceView[];

/**
 * Each view stacks one unit on the main axis and shows one ratio below it on its
 * own axis, so no chart ever needs a second y-axis. The first lower metric is the
 * view's default.
 */
export const PERFORMANCE_LOWER_METRICS: Record<
  PerformanceView,
  readonly DashboardPerformanceMetric[]
> = {
  sales: ["acos", "tacos", "roas"],
  orders: ["cvr", "cpa"],
};

export interface MetricFormatters {
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

/** Card / menu / tooltip value: whole currency once it passes 1,000, cents below. */
export function formatMetricValue(
  metric: DashboardPerformanceMetric,
  value: number | null | undefined,
  currency: string | null | undefined,
  { formatNumber }: MetricFormatters,
): string {
  if (value == null) return "—";
  switch (METRIC_FAMILY[metric]) {
    case "currency": {
      if (currency === null) return "—";
      const whole = Math.abs(value) >= 1000;
      return formatNumber(value, {
        style: "currency",
        currency: currency ?? "USD",
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      });
    }
    case "percent":
      return formatNumber(value, {
        style: "percent",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    case "ratio":
      return `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
    default:
      return Math.abs(value) >= 1_000_000
        ? formatNumber(value, { notation: "compact", maximumFractionDigits: 1 })
        : formatNumber(value, { maximumFractionDigits: 0 });
  }
}

/** Axis tick: compact, no currency cents, and a bare "0" at the baseline. */
export function formatAxisTick(
  family: DashboardMetricFamily,
  value: number,
  currency: string | null | undefined,
  { formatNumber }: MetricFormatters,
): string {
  if (family === "percent") {
    return formatNumber(value, { style: "percent", maximumFractionDigits: 0 });
  }
  if (value === 0) return "0";
  if (family === "currency") {
    return formatNumber(value, {
      style: "currency",
      currency: currency ?? "USD",
      currencyDisplay: "narrowSymbol",
      notation: "compact",
      maximumFractionDigits: 1,
    });
  }
  return formatNumber(value, { notation: "compact", maximumFractionDigits: 1 });
}

export type DeltaTone = "improvement" | "worsening" | "neutral";

export interface KpiDelta {
  direction: "up" | "down" | "flat";
  /** Points for percent metrics, otherwise a relative change ratio. */
  kind: "points" | "relative";
  magnitude: number;
  tone: DeltaTone;
}

/**
 * Percent metrics compare in percentage points (ACoS 27.9% → 25.8% is −2.1 pts),
 * everything else relatively. `semantics` carries "lower is better" for spend,
 * ACoS, TACoS, CPC and CPA, so a drop there is an improvement.
 */
export function kpiDelta(kpi: PerformanceKpi<DashboardPerformanceMetric>): KpiDelta | null {
  const { value, previousValue } = kpi;
  if (value == null || previousValue == null) return null;
  const kind = METRIC_FAMILY[kpi.key] === "percent" ? "points" : "relative";
  if (kind === "relative" && previousValue === 0) return null;
  const raw =
    kind === "points"
      ? (value - previousValue) * 100
      : (value - previousValue) / Math.abs(previousValue);
  const direction = raw === 0 ? "flat" : raw > 0 ? "up" : "down";
  const tone: DeltaTone =
    direction === "flat" || kpi.semantics === "neutral"
      ? "neutral"
      : (kpi.semantics === "positive-is-good") === (direction === "up")
        ? "improvement"
        : "worsening";
  return { direction, kind, magnitude: Math.abs(raw), tone };
}

/** Rounds the axis maximum up to a 1/2/2.5/5 × 10ⁿ step so four gridlines read cleanly. */
export function niceTicks(max: number, segments = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1, 2, 3, 4].map((tick) => tick / segments);
  const rough = max / segments;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step =
    [1, 2, 2.5, 5, 10]
      .map((factor) => factor * magnitude)
      .find((candidate) => candidate >= rough) ?? 10 * magnitude;
  return Array.from({ length: segments + 1 }, (_, index) => index * step);
}
